import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import PostCard from "../components/PostCard";
import SearchBar from "../components/SearchBar";
import SEO from "../components/SEO";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { COUNTRIES } from "../lib/countries";
import { Lightbulb, X } from "@phosphor-icons/react";

const PAGE_SIZE = 30;

export default function Feed() {
    const [params, setParams] = useSearchParams();
    const [categories, setCategories] = useState([]);
    const [posts, setPosts] = useState([]);
    const [sort, setSort] = useState(params.get("sort") || "latest");
    const [category, setCategory] = useState(params.get("category") || "all");
    // Community Ideas feed is GLOBAL by default — every user-posted idea
    // is visible across every viewer's account regardless of their own
    // country. Viewers can still narrow with the country dropdown below,
    // but the initial state shows the entire community wall. (Admin
    // curated ideas on Home stay country-scoped — that's by design.)
    const [country, setCountry] = useState(params.get("country") || "");
    // Optional: filter feed to a single author (used when clicking a
    // "person" result from the search dropdown).
    const [authorId, setAuthorId] = useState(params.get("author_id") || "");
    const [authorName, setAuthorName] = useState("");
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const skipRef = useRef(0);
    const loadingMoreRef = useRef(false);
    const hasMoreRef = useRef(true);
    const sentinelRef = useRef(null);

    // Load categories once
    useEffect(() => {
        api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    }, []);

    // Keep the local authorId state in sync when the URL changes (e.g.
    // the user clicks a different person in the search dropdown while
    // already on /feed).
    useEffect(() => {
        setAuthorId(params.get("author_id") || "");
    }, [params]);

    // Build query string for the URL bar
    useEffect(() => {
        const sp = {};
        if (sort) sp.sort = sort;
        if (category !== "all") sp.category = category;
        if (country) sp.country = country;
        if (authorId) sp.author_id = authorId;
        setParams(sp, { replace: true });
    }, [sort, category, country, authorId, setParams]);

    // Fetch posts — first page or next page
    const fetchPage = useCallback(async (reset) => {
        if (reset) {
            setLoading(true);
            skipRef.current = 0;
            hasMoreRef.current = true;
            setHasMore(true);
        } else {
            if (loadingMoreRef.current || !hasMoreRef.current) return;
            loadingMoreRef.current = true;
            setLoadingMore(true);
        }
        const q = {
            sort,
            limit: PAGE_SIZE,
            skip: skipRef.current,
            author_role: "user",
        };
        if (category && category !== "all") q.category = category;
        if (country) q.country = country;
        if (authorId) q.author_id = authorId;
        try {
            const { data } = await api.get("/posts", { params: q });
            setPosts((prev) => (reset ? data : [...prev, ...data]));
            skipRef.current += data.length;
            if (data.length < PAGE_SIZE) {
                hasMoreRef.current = false;
                setHasMore(false);
            }
            // Capture the author's name from the first result so the
            // "Showing posts by <name>" chip has something to render.
            if (reset && authorId && data.length > 0) {
                setAuthorName(data[0].author_name || "this user");
            } else if (reset && !authorId) {
                setAuthorName("");
            }
        } finally {
            if (reset) setLoading(false);
            loadingMoreRef.current = false;
            setLoadingMore(false);
        }
    }, [sort, category, country, authorId]);

    // Reload when filters change
    useEffect(() => {
        fetchPage(true);
        // eslint-disable-next-line
    }, [sort, category, country, authorId]);

    // IntersectionObserver for infinite scroll
    useEffect(() => {
        const node = sentinelRef.current;
        if (!node) return;
        const io = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) fetchPage(false);
        }, { rootMargin: "400px" });
        io.observe(node);
        return () => io.disconnect();
    }, [fetchPage]);

    const handleDelete = (id) => setPosts((prev) => prev.filter((p) => p.id !== id));

    return (
        <div className="px-4 sm:px-6 lg:px-10 py-8 lg:py-12 max-w-6xl mx-auto" data-testid="feed-page">
            <SEO
                title="Community startup ideas"
                description="Discover startup ideas posted by the Ideas Mine community. Sort by trending, latest, most discussed, or featured. Filter by country and category."
                path="/feed"
            />
            <header className="mb-8 flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <h1 className="font-display font-black text-3xl lg:text-5xl tracking-tighter mb-2">
                        <span className="text-[#6C63FF]">Post Idea,</span> <span className="text-[#FF8A00]">Get Funding</span>
                    </h1>
                    <p className="text-muted-foreground">Share your startup ideas with the Ideas Mine community and connect with investors — filter by country, category and sort.</p>
                </div>
                <div className="flex-shrink-0 pt-2">
                    <SearchBar />
                </div>
            </header>

            {authorId && (
                <div className="mb-6 flex items-center gap-3" data-testid="feed-author-filter-chip">
                    <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-sm">
                        <span className="text-muted-foreground">Showing posts by</span>
                        <span className="font-semibold">{authorName || "this user"}</span>
                        <button
                            type="button"
                            onClick={() => setAuthorId("")}
                            className="ml-1 h-5 w-5 flex items-center justify-center rounded-full hover:bg-accent transition-colors"
                            aria-label="Clear author filter"
                            data-testid="feed-author-filter-clear"
                        >
                            <X size={12} />
                        </button>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-6">
                <div className="lg:col-span-8">
                    <Tabs value={sort} onValueChange={setSort}>
                        <TabsList data-testid="feed-tabs">
                            <TabsTrigger value="trending" data-testid="feed-sort-trending">Trending</TabsTrigger>
                            <TabsTrigger value="latest" data-testid="feed-sort-latest">Latest</TabsTrigger>
                            <TabsTrigger value="discussed" data-testid="feed-sort-discussed">Discussed</TabsTrigger>
                            <TabsTrigger value="featured" data-testid="feed-sort-featured">Featured</TabsTrigger>
                        </TabsList>
                    </Tabs>
                </div>
                <div className="lg:col-span-4 flex gap-2">
                    <Select value={country || "any"} onValueChange={(v) => setCountry(v === "any" ? "" : v)}>
                        <SelectTrigger data-testid="filter-country"><SelectValue placeholder="Country" /></SelectTrigger>
                        <SelectContent className="max-h-72">
                            <SelectItem value="any">Any country</SelectItem>
                            {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={category} onValueChange={setCategory}>
                        <SelectTrigger data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
                        <SelectContent className="max-h-72">
                            <SelectItem value="all">All categories</SelectItem>
                            {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {loading ? (
                <div className="grid gap-4">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="h-48 rounded-2xl border border-border bg-card animate-pulse" />
                    ))}
                </div>
            ) : posts.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border p-12 text-center">
                    <Lightbulb size={36} weight="duotone" className="mx-auto text-muted-foreground mb-3" />
                    <h3 className="font-display font-bold text-lg">No ideas match your filters</h3>
                </div>
            ) : (
                <>
                    <div className="grid gap-5">
                        {posts.map((p) => (
                            <PostCard key={p.id} post={p} onDelete={handleDelete} />
                        ))}
                    </div>

                    <div ref={sentinelRef} className="h-12" data-testid="feed-infinite-sentinel" />

                    {loadingMore && (
                        <div className="grid gap-4 mt-4" data-testid="feed-loading-more">
                            {[1, 2].map((i) => (
                                <div key={i} className="h-32 rounded-2xl border border-border bg-card animate-pulse" />
                            ))}
                        </div>
                    )}

                    {!hasMore && posts.length > PAGE_SIZE && (
                        <div className="text-center text-sm text-muted-foreground mt-8 py-6 border-t border-border" data-testid="feed-end-marker">
                            You've reached the end · {posts.length} ideas
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
